import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { exchangeLaunch, loginTeacher } from "../api/auth";
import { clearTeacherCache, primeTeacherCache } from "../hooks/useTeacher";
import {
  ApiError,
  clearStoredAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
} from "../api/client";
import {
  clearLaunchParamsFromUrl,
  readLaunchParams,
} from "../utils/tutorcloudLaunch";
import { normalizeTeacherAccessibility } from "../utils/teacherAccessibility";
import {
  LAUNCH_MODE_FULL_ACCESS,
  LAUNCH_MODE_TEMA_ONLY,
  canReturnToTutorCloud,
} from "../utils/tutorcloudAccess";

// Dedupe the launch exchange per raw credential for the lifetime of the page.
// React StrictMode runs the bootstrap effect twice (setup → cleanup → setup);
// without this, two /auth/launch calls fire for the same code/token and the second
// would be rejected (single-use). Sharing one promise guarantees a single request.
const launchExchangeCache = new Map();

function launchCacheKey(params) {
  if (params?.code) return `code:${params.code}`;
  if (params?.token) return `token:${params.token}`;
  return "";
}

function exchangeLaunchOnce(params) {
  const key = launchCacheKey(params);
  if (!key) {
    return Promise.reject(new Error("Missing launch code or token"));
  }
  if (!launchExchangeCache.has(key)) {
    launchExchangeCache.set(
      key,
      exchangeLaunch({
        code: params.code,
        launchToken: params.token,
      }),
    );
  }
  return launchExchangeCache.get(key);
}

const STORAGE_KEY = "tema_teacher_id";
const DASHBOARD_URL_KEY = "tema_dashboard_url";
const APP_ORIGIN_KEY = "tema_app_origin";
const ACCESSIBILITY_KEY = "tema_accessibility";
const LAUNCH_MODE_KEY = "tema_launch_mode";
const ACCOUNT_STATUS_KEY = "tema_account_status";

export function getStoredTeacherId() {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY)?.trim();
    return value || undefined;
  } catch {
    return undefined;
  }
}

export function getStoredDashboardUrl() {
  try {
    return sessionStorage.getItem(DASHBOARD_URL_KEY)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

export function getStoredAppOrigin() {
  try {
    return sessionStorage.getItem(APP_ORIGIN_KEY)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

function persistTeacherId(teacherId) {
  sessionStorage.setItem(STORAGE_KEY, teacherId);
}

function readStoredLaunchMode() {
  try {
    return sessionStorage.getItem(LAUNCH_MODE_KEY)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

function readStoredAccountStatus() {
  try {
    return sessionStorage.getItem(ACCOUNT_STATUS_KEY)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

function persistLaunchSession({ dashboardUrl, appOrigin, launchMode, accountStatus }) {
  persistNavigationUrls(dashboardUrl, appOrigin);
  if (launchMode) {
    sessionStorage.setItem(LAUNCH_MODE_KEY, launchMode);
  } else {
    sessionStorage.removeItem(LAUNCH_MODE_KEY);
  }
  if (accountStatus) {
    sessionStorage.setItem(ACCOUNT_STATUS_KEY, accountStatus);
  } else {
    sessionStorage.removeItem(ACCOUNT_STATUS_KEY);
  }
}

function persistNavigationUrls(dashboardUrl, appOrigin) {
  if (dashboardUrl) {
    sessionStorage.setItem(DASHBOARD_URL_KEY, dashboardUrl);
  } else {
    sessionStorage.removeItem(DASHBOARD_URL_KEY);
  }
  if (appOrigin) {
    sessionStorage.setItem(APP_ORIGIN_KEY, appOrigin);
  } else {
    sessionStorage.removeItem(APP_ORIGIN_KEY);
  }
}

function readStoredAccessibility() {
  try {
    const raw = sessionStorage.getItem(ACCESSIBILITY_KEY);
    if (!raw) return undefined;
    return normalizeTeacherAccessibility(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

function persistAccessibility(accessibility) {
  if (!accessibility) {
    sessionStorage.removeItem(ACCESSIBILITY_KEY);
    return;
  }
  sessionStorage.setItem(ACCESSIBILITY_KEY, JSON.stringify(accessibility));
}

function clearStoredTeacherId() {
  sessionStorage.removeItem(STORAGE_KEY);
  clearStoredAccessToken();
  sessionStorage.removeItem(DASHBOARD_URL_KEY);
  sessionStorage.removeItem(APP_ORIGIN_KEY);
  sessionStorage.removeItem(ACCESSIBILITY_KEY);
  sessionStorage.removeItem(LAUNCH_MODE_KEY);
  sessionStorage.removeItem(ACCOUNT_STATUS_KEY);
}

const AuthContext = createContext(null);

function isSessionAuthenticated() {
  return Boolean(getStoredTeacherId() && getStoredAccessToken());
}

export function AuthProvider({ children }) {
  const [teacherId, setTeacherId] = useState(() =>
    isSessionAuthenticated() ? getStoredTeacherId() : undefined,
  );
  const [dashboardUrl, setDashboardUrl] = useState(() => getStoredDashboardUrl());
  const [appOrigin, setAppOrigin] = useState(() => getStoredAppOrigin());
  const [launchMode, setLaunchMode] = useState(
    () => readStoredLaunchMode() || LAUNCH_MODE_TEMA_ONLY,
  );
  const [accountStatus, setAccountStatus] = useState(
    () => readStoredAccountStatus() || "active",
  );
  const [accessibility, setAccessibility] = useState(() => readStoredAccessibility());
  const [loginError, setLoginError] = useState(null);
  const [launchError, setLaunchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLaunching, setIsLaunching] = useState(() => {
    if (isSessionAuthenticated()) {
      return false;
    }
    const params = readLaunchParams();
    return Boolean(params?.code || params?.token);
  });

  useEffect(() => {
    if (isSessionAuthenticated()) {
      // A session already exists (e.g. TEMA was reopened in a tab that is still
      // signed in). Drop any leftover launch token from the URL so it cannot be
      // replayed after a later logout + refresh.
      clearLaunchParamsFromUrl();
      return undefined;
    }

    const launchParams = readLaunchParams();
    if (!launchParams?.code && !launchParams?.token) {
      setIsLaunching(false);
      return undefined;
    }

    let cancelled = false;

    async function bootstrapFromLaunch() {
      setIsLaunching(true);
      setLaunchError(null);
      try {
        const result = await exchangeLaunchOnce(launchParams);
        if (cancelled) {
          return;
        }
        setStoredAccessToken(result.accessToken);
        persistTeacherId(result.teacherId);
        persistLaunchSession({
          dashboardUrl: result.dashboardUrl,
          appOrigin: result.appOrigin,
          launchMode: result.launchMode,
          accountStatus: result.accountStatus,
        });
        const nextAccessibility = normalizeTeacherAccessibility(result.accessibility);
        persistAccessibility(nextAccessibility);
        setTeacherId(result.teacherId);
        setDashboardUrl(result.dashboardUrl || undefined);
        setAppOrigin(result.appOrigin || undefined);
        setLaunchMode(result.launchMode || LAUNCH_MODE_TEMA_ONLY);
        setAccountStatus(result.accountStatus || "active");
        setAccessibility(nextAccessibility);
        primeTeacherCache(result.teacherId, result.teacher);
        setLaunchError(null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Could not sign in from TutorCloud. Please try again.";
        setLaunchError(message);
      } finally {
        clearLaunchParamsFromUrl();
        if (!cancelled) {
          setIsLaunching(false);
        }
      }
    }

    bootstrapFromLaunch();

    return () => {
      cancelled = true;
    };
  }, []);

  const establishSession = useCallback((result) => {
    setStoredAccessToken(result.accessToken);
    persistTeacherId(result.teacherId);
    persistLaunchSession({
      dashboardUrl: result.dashboardUrl,
      appOrigin: result.appOrigin,
      launchMode: result.launchMode,
      accountStatus: result.accountStatus,
    });
    const nextAccessibility = normalizeTeacherAccessibility(result.accessibility);
    persistAccessibility(nextAccessibility);
    setTeacherId(result.teacherId);
    setDashboardUrl(result.dashboardUrl || undefined);
    setAppOrigin(result.appOrigin || undefined);
    setLaunchMode(result.launchMode || LAUNCH_MODE_TEMA_ONLY);
    setAccountStatus(result.accountStatus || "active");
    setAccessibility(nextAccessibility);
    if (result.teacher) {
      primeTeacherCache(result.teacherId, result.teacher);
    }
    return result;
  }, []);

  const login = useCallback(async (email, password) => {
    setLoginError(null);
    setLaunchError(null);
    setIsSubmitting(true);
    try {
      const result = await loginTeacher(email, password);
      return establishSession(result);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Sign in failed. Please try again.";
      setLoginError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [establishSession]);

  const logout = useCallback(() => {
    // Remove any lingering launch token from the URL so refreshing after logout
    // does not re-exchange it (which would either sign the user back in or show
    // the "launch link has already been used" error instead of the login page).
    clearLaunchParamsFromUrl();
    clearStoredTeacherId();
    clearTeacherCache();
    setTeacherId(undefined);
    setDashboardUrl(undefined);
    setAppOrigin(undefined);
    setLaunchMode(LAUNCH_MODE_TEMA_ONLY);
    setAccountStatus("active");
    setAccessibility(undefined);
    setLoginError(null);
    setLaunchError(null);
  }, []);

  const canReturnToTc = useMemo(
    () => canReturnToTutorCloud({ launchMode, dashboardUrl, accountStatus }),
    [launchMode, dashboardUrl, accountStatus],
  );

  const value = useMemo(
    () => ({
      teacherId,
      isAuthenticated: Boolean(teacherId && getStoredAccessToken()),
      login,
      establishSession,
      logout,
      loginError,
      launchError,
      isSubmitting,
      isLaunching,
      dashboardUrl,
      appOrigin,
      launchMode,
      accountStatus,
      canReturnToTutorCloud: canReturnToTc,
      accessibility,
      clearLoginError: () => setLoginError(null),
      clearLaunchError: () => setLaunchError(null),
    }),
    [
      teacherId,
      login,
      establishSession,
      logout,
      loginError,
      launchError,
      isSubmitting,
      isLaunching,
      dashboardUrl,
      appOrigin,
      launchMode,
      accountStatus,
      canReturnToTc,
      accessibility,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
